import { createElement, getUiRoot, setText } from './dom';
import { setLabel } from './i18n';
import type { PeerPose } from '../multiplayer/peers';

interface PlayerActions {
  whisper: (peer: PeerPose) => void;
  follow: (peer: PeerPose) => void;
  muted: (id: string) => boolean;
  mute: (id: string, muted: boolean) => Promise<void>;
}

function menuElements() {
  const menu = createElement('div', 'player-context', getUiRoot());
  menu.hidden = true;
  menu.setAttribute('role', 'menu');
  setLabel(menu, 'aria-label', 'Selected visitor');
  const button = (label: string) => {
    const node = createElement('button', '', menu);
    node.type = 'button';
    node.setAttribute('role', 'menuitem');
    node.tabIndex = -1;
    setText(node, label);
    return node;
  };
  const buttons = [button('Whisper'), button('Follow'), button('Mute')] as const;
  const error = createElement('p', 'player-context-error', menu);
  error.hidden = true;
  return { menu, buttons, error };
}

class PlayerContextMenu {
  private readonly elements = menuElements();
  private target: PeerPose | null = null;
  private pending = false;

  constructor(
    private readonly changed: (open: boolean) => void,
    private readonly actions: PlayerActions
  ) {
    const [whisper, follow, mute] = this.elements.buttons;
    whisper.addEventListener('click', () => {
      this.choose('whisper');
    });
    follow.addEventListener('click', () => {
      this.choose('follow');
    });
    mute.addEventListener('click', () => {
      void this.changeMute();
    });
    document.addEventListener('pointerdown', this.outside, true);
    document.addEventListener('keydown', this.key, true);
  }

  get isOpen(): boolean {
    return !this.elements.menu.hidden;
  }

  show(peer: PeerPose, x: number, y: number): void {
    if (this.pending) return;
    const { menu, buttons, error } = this.elements;
    this.target = peer;
    menu.dataset.player = peer.id;
    setText(buttons[2], this.actions.muted(peer.id) ? 'Unmute' : 'Mute');
    error.hidden = true;
    menu.hidden = false;
    menu.style.left = `${Math.max(8, Math.min(x, innerWidth - menu.offsetWidth - 8))}px`;
    menu.style.top = `${Math.max(8, Math.min(y, innerHeight - menu.offsetHeight - 8))}px`;
    this.changed(true);
    buttons[0].focus();
  }

  hide(): void {
    if (!this.isOpen) return;
    this.elements.menu.hidden = true;
    this.changed(false);
  }

  dispose(): void {
    this.hide();
    document.removeEventListener('pointerdown', this.outside, true);
    document.removeEventListener('keydown', this.key, true);
    this.elements.menu.remove();
  }

  private choose(action: 'whisper' | 'follow'): void {
    const peer = this.target;
    this.hide();
    if (peer) this.actions[action](peer);
  }

  private async changeMute(): Promise<void> {
    const peer = this.target;
    if (!peer || this.pending) return;
    const { error, buttons } = this.elements;
    this.pending = true;
    error.hidden = true;
    for (const button of buttons) button.disabled = true;
    try {
      await this.actions.mute(peer.id, !this.actions.muted(peer.id));
      this.hide();
    } catch (reason) {
      setText(
        error,
        reason instanceof Error ? reason.message : 'The city did not confirm this change.'
      );
      error.hidden = false;
      error.setAttribute('role', 'alert');
    } finally {
      this.pending = false;
      for (const button of buttons) button.disabled = false;
    }
  }

  private readonly outside = (event: PointerEvent): void => {
    if (event.target instanceof Node && !this.elements.menu.contains(event.target)) this.hide();
  };

  private readonly key = (event: KeyboardEvent): void => {
    if (
      !this.isOpen ||
      !['Escape', 'ArrowDown', 'ArrowUp', 'Home', 'End', 'Tab'].includes(event.key)
    )
      return;
    event.preventDefault();
    event.stopImmediatePropagation();
    if (event.key === 'Escape' || event.key === 'Tab') {
      this.hide();
      return;
    }
    const buttons = this.elements.buttons;
    const current = buttons.indexOf(document.activeElement as HTMLButtonElement);
    const index =
      event.key === 'Home'
        ? 0
        : event.key === 'End'
          ? 2
          : (current + (event.key === 'ArrowUp' ? 2 : 1)) % 3;
    buttons[index]?.focus();
  };
}

export function createPlayerContextMenu(changed: (open: boolean) => void, actions: PlayerActions) {
  return new PlayerContextMenu(changed, actions);
}
